import { AnimatingCircle } from '@inbox-demo-nx/ui-animating-circle';
import CircleProgress from '@inbox-demo-nx/ui-circle-progress';
import { Text, useDripsyTheme } from 'dripsy';
import { useEffect, useMemo } from 'react';
import { Pressable, PressableProps, TextStyle, ViewStyle } from 'react-native';
import Animated, {
  useAnimatedStyle,
  useSharedValue,
  withTiming,
} from 'react-native-reanimated';

const sizeMap = {
  small: 26,
  default: 44,
  large: 56,
};

const paddingHorizontalMap = {
  small: 12,
  default: 12,
  large: 16,
};

const fontSizeMap = {
  small: 12,
  default: 16,
  large: 16,
};

const progressSizeMap = {
  small: 12,
  default: 24,
  large: 24,
};

const useColorMap = () => {
  const { theme } = useDripsyTheme();

  return useMemo(() => {
    const colorMap = {
      primary: {
        background: theme.colors.primaryButtonBackgroundDefault,
        backgroundHovered: theme.colors.primaryButtonBackgroundHover,
        content: theme.colors.primaryButtonContent,
      },
      secondary: {
        background: theme.colors.secondaryButtonBackgroundDefault,
        backgroundHovered: theme.colors.secondaryButtonBackgroundHover,
        content: theme.colors.secondaryButtonContent,
      },
      disabled: {
        background: theme.colors.disabledButtonBackground,
        backgroundHovered: theme.colors.disabledButtonBackground,
        content: theme.colors.disabledButtonContent,
      },
    };
    return colorMap;
  }, [theme]);
};

type Props = {
  title: string;
  variant?: 'primary' | 'secondary';
  size?: 'default' | 'small' | 'large';
  isLoading?: boolean;
  rounded?: boolean;
  textStyle?: TextStyle;
  buttonStyle?: ViewStyle;
  animatingCircleId?: string;
} & PressableProps;

export default function Button({
  title,
  variant = 'primary',
  size = 'default',
  isLoading,
  rounded = false,
  disabled,
  textStyle,
  buttonStyle,
  animatingCircleId,
  style,
  ...props
}: Props) {
  const colorMap = useColorMap();
  const {
    background,
    backgroundHovered,
    content: contentColor,
  } = colorMap[disabled ? 'disabled' : variant];

  const backgroundColor = useSharedValue(background);
  const scale = useSharedValue(1);
  const animatedStyles = useAnimatedStyle(() => ({
    backgroundColor: backgroundColor.value,
    transform: [{ scale: withTiming(scale.value, { duration: 100 }) }],
  }));

  useEffect(() => {
    const { background } = colorMap[disabled ? 'disabled' : variant];
    backgroundColor.value = background;
  }, [backgroundColor, colorMap, disabled, variant]);

  const paddingHorizontal =
    paddingHorizontalMap[size] || paddingHorizontalMap.default;
  const height = sizeMap[size] || sizeMap.default;

  function getRadius() {
    if (rounded) {
      return height / 2;
    }
    return Math.min(8, height / 5);
  }

  return (
    <Pressable
      disabled={disabled || isLoading}
      // @ts-ignore
      onHoverIn={() => {
        backgroundColor.value = withTiming(backgroundHovered);
      }}
      onHoverOut={() => {
        backgroundColor.value = withTiming(background);
      }}
      onPressIn={() => {
        scale.value = 0.98;
        backgroundColor.value = withTiming(backgroundHovered);
      }}
      onPressOut={() => {
        scale.value = 1;
        backgroundColor.value = withTiming(background);
      }}
      style={[{ position: 'relative' }, style as any]}
      {...props}
    >
      <Animated.View
        style={[
          animatedStyles,
          {
            paddingHorizontal,
            height,
            alignItems: 'center',
            justifyContent: 'center',
            borderRadius: getRadius(),
          },
          buttonStyle,
        ]}
      >
        {isLoading ? (
          <CircleProgress
            size={progressSizeMap[size] || progressSizeMap.default}
            color={contentColor}
          />
        ) : (
          <Text
            sx={{
              textAlign: 'center',
              fontSize: fontSizeMap[size] || fontSizeMap.default,
              color: contentColor,
              variant: 'styles.buttonText',
              userSelect: 'none',
            }}
            style={textStyle}
          >
            {title}
          </Text>
        )}
      </Animated.View>
      {animatingCircleId ? <AnimatingCircle id={animatingCircleId} /> : null}
    </Pressable>
  );
}
